import {
  AttachMoney,
  Calculate,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { getDollarValue, getUfValue } from 'lib/api/cmf';
import { getCreditLineByProduct } from 'lib/api/creditLine';
import { createSGRWarrantyRequest } from 'lib/api/sgr';
import CustomButton from 'lib/designSystem/Button';
import CustomDialogTitle from 'lib/designSystem/DialogTitle';
import riskPremiumCalculator, {
  Output,
  RiskPremiumRates,
} from 'lib/functions/riskPremiumCalculator';
import { formatCurrency, formatMoney } from 'lib/functions/utils';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { WarrantyFormData } from './CreateWarrantyDialog';

type CalculationResult = Output;

interface WarrantyCreationConfirmationProps {
  open: boolean;
  formData: WarrantyFormData;
  rateControls: RiskPremiumRates;
  onClose: () => void;
  fetchWarranties: () => void;
}

const WarrantyCreationConfirmation: React.FC<
  WarrantyCreationConfirmationProps
> = ({ open, formData, rateControls, onClose, fetchWarranties }) => {
  const dispatch = useDispatch();
  const { data: dollar } = getDollarValue(dispatch);
  const { data: uf } = getUfValue(dispatch);
  const { mutate, isPending } = createSGRWarrantyRequest(dispatch);
  const { calculate } = riskPremiumCalculator();
  const [originalCalculation, setOriginalCalculation] =
    useState<CalculationResult | null>(null);
  const [modifiedCalculation, setModifiedCalculation] =
    useState<CalculationResult | null>(null);
  const { data: creditLineData } = getCreditLineByProduct(
    dispatch,
    formData.business?.identifier,
    'sgr',
  );

  const { quotedAmount, quotedCurrency, startDate, endDate, type } = formData;
  const amount = Number(quotedAmount);

  const originalRates = useMemo(() => {
    const subProduct = creditLineData?.subProducts?.find(
      (sp) => sp.subProduct.id === type,
    );

    return {
      variableRate: subProduct?.operationCostVariable,
      fixedRate: subProduct?.operationCostFixed,
      variableDiscount: subProduct?.discountRateVariable,
      fixedDiscount: subProduct?.discountRateFixed,
    };
  }, [type]);

  const handleConfirm = async () => {
    const conversionRates = { UF: uf?.value, USD: dollar?.value, CLP: 1 };
    const body = {
      ...formData,
      status: 'IN_REVIEW',
      quotedAmount: Number(formData.quotedAmount),
      conversionRate: conversionRates[formData.quotedCurrency],
      businessIdentifier: formData.business?.identifier,
      operationCostFixed: rateControls.fixedRate,
      operationCostVariable: rateControls.variableRate,
      discountRateFixed: rateControls.fixedDiscount,
      discountRateVariable: rateControls.variableDiscount,
      isUserGeneratedQuote: false,
    };
    mutate(body, {
      onSuccess: () => {
        onClose();
        fetchWarranties();
      },
    });
  };

  useEffect(() => {
    const performCalculations = () => {
      try {
        const original = calculate({
          quotedAmount: amount,
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
          quotedCurrency,
          rates: originalRates,
        });

        const modified = calculate({
          quotedAmount: amount,
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
          quotedCurrency,
          rates: rateControls,
        });

        setOriginalCalculation(original);
        setModifiedCalculation(modified);
      } catch (error) {
        console.error(error);
        showAlert(dispatch, 'error', 'Error al calcular el costo de emisión');
        setOriginalCalculation(null);
        setModifiedCalculation(null);
      }
    };

    performCalculations();
  }, [amount, startDate, endDate, quotedCurrency, type, rateControls]);

  const originalPremium = originalCalculation?.amount || 0;
  const modifiedPremium = modifiedCalculation?.amount || 0;
  const premiumDifference = modifiedPremium - originalPremium;
  const hasRateChanges = premiumDifference !== 0;

  const amountFormatted = formatCurrency(amount, quotedCurrency);
  const conversionRate = quotedCurrency === 'UF' ? uf?.value : dollar?.value;
  const amountInCLP = amount * (conversionRate ?? 1);
  const showCLPBreakdown = quotedCurrency === 'UF' || quotedCurrency === 'USD';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          margin: '20px 20px',
        },
      }}
    >
      <DialogContent sx={{ px: 3 }}>
        <CustomDialogTitle setOpen={onClose}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                backgroundColor: hasRateChanges
                  ? 'warning.main'
                  : 'success.main',
                borderRadius: '50%',
                p: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}
            >
              <Calculate sx={{ fontSize: '1rem' }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '1.1rem',
              }}
            >
              Confirmación de Creación de Garantía
            </Typography>
            <Chip
              label={hasRateChanges ? 'Tasas Modificadas' : 'Tasas Estándar'}
              size="small"
              color={hasRateChanges ? 'warning' : 'success'}
              variant="outlined"
              sx={{ height: '20px', fontSize: '0.7rem' }}
            />
          </Stack>
        </CustomDialogTitle>
        <Grid container spacing={3}>
          {/* Main Info */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AttachMoney
                    sx={{ fontSize: '0.8rem', color: 'success.main' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Monto de Garantía
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: '0.85rem' }}
                  >
                    {amountFormatted}
                  </Typography>
                  {showCLPBreakdown && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.6rem', display: 'block', mt: 0.5 }}
                    >
                      ≈ {formatMoney(amountInCLP)} CLP
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Calculate
                    sx={{ fontSize: '0.8rem', color: 'primary.main' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Costo de emisión
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: '0.85rem' }}
                  >
                    {formatMoney(modifiedPremium)} CLP
                  </Typography>
                </Box>
              </Box>

              {hasRateChanges && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      backgroundColor:
                        premiumDifference > 0
                          ? 'rgba(76, 175, 80, 0.1)'
                          : 'rgba(244, 67, 54, 0.1)',
                      borderRadius: '50%',
                      p: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {premiumDifference > 0 ? (
                      <TrendingUpIcon
                        sx={{ fontSize: '0.8rem', color: 'success.main' }}
                      />
                    ) : (
                      <TrendingDownIcon
                        sx={{ fontSize: '0.8rem', color: 'error.main' }}
                      />
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.65rem' }}
                    >
                      Variación de costo de emisión
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{
                        fontSize: '0.85rem',
                        color:
                          premiumDifference > 0 ? 'success.main' : 'error.main',
                      }}
                    >
                      {premiumDifference > 0 ? '+' : ''}
                      {formatMoney(premiumDifference)} CLP
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Rate Changes */}
          {hasRateChanges && (
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: '0.8rem', mb: 1 }}
                >
                  Cambios en Tasas:
                </Typography>

                {rateControls.variableRate !== originalRates.variableRate && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      Tasa Variable:
                    </Typography>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {originalRates.variableRate?.toFixed(2)}%
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                        →
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{
                          fontSize: '0.7rem',
                          color:
                            (rateControls.variableRate ?? 0) >
                            (originalRates.variableRate ?? 0)
                              ? 'success.main'
                              : 'error.main',
                        }}
                      >
                        {rateControls.variableRate?.toFixed(2)}%
                      </Typography>
                    </Box>
                  </Box>
                )}

                {rateControls.fixedRate !== originalRates.fixedRate && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      Tasa Fija:
                    </Typography>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {originalRates.fixedRate?.toFixed(2)} UF
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                        →
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{
                          fontSize: '0.7rem',
                          color:
                            (rateControls.fixedRate ?? 0) >
                            (originalRates.fixedRate ?? 0)
                              ? 'success.main'
                              : 'error.main',
                        }}
                      >
                        {rateControls.fixedRate?.toFixed(2)} UF
                      </Typography>
                    </Box>
                  </Box>
                )}

                {rateControls.variableDiscount !==
                  originalRates.variableDiscount && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      Descuento Variable:
                    </Typography>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {originalRates.variableDiscount?.toFixed(2)}%
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                        →
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{
                          fontSize: '0.7rem',
                          color:
                            (rateControls.variableDiscount ?? 0) >
                            (originalRates.variableDiscount ?? 0)
                              ? 'error.main'
                              : 'success.main',
                        }}
                      >
                        {rateControls.variableDiscount?.toFixed(2)}%
                      </Typography>
                    </Box>
                  </Box>
                )}

                {rateControls.fixedDiscount !== originalRates.fixedDiscount && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      Descuento Fijo:
                    </Typography>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {originalRates.fixedDiscount?.toFixed(2)}%
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                        →
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{
                          fontSize: '0.7rem',
                          color:
                            (rateControls.fixedDiscount ?? 0) >
                            (originalRates.fixedDiscount ?? 0)
                              ? 'error.main'
                              : 'success.main',
                        }}
                      >
                        {rateControls.fixedDiscount?.toFixed(2)}%
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          )}
        </Grid>

        {hasRateChanges && (
          <Alert
            severity="warning"
            icon={<WarningIcon sx={{ fontSize: '0.9rem' }} />}
            sx={{
              mt: 2,
              borderRadius: 0.5,
              backgroundColor: 'rgba(255, 152, 0, 0.08)',
              border: '1px solid rgba(255, 152, 0, 0.2)',
              py: 0.75,
              '& .MuiAlert-message': { py: 0.25 },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
              Tasas Modificadas
            </AlertTitle>
            <Typography
              variant="body2"
              sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}
            >
              Las tasas han sido modificadas respecto a la cotización original.
              El costo de emisión ha variado en{' '}
              {formatMoney(Math.abs(premiumDifference))} CLP (
              {premiumDifference > 0 ? 'incremento' : 'reducción'}).
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'flex-end', gap: 2 }}>
        <CustomButton color="secondary" onClick={onClose}>
          Cancelar
        </CustomButton>
        <CustomButton
          color="primary"
          onClick={handleConfirm}
          disabled={isPending}
          loading={isPending}
        >
          Confirmar y Crear Garantía
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default WarrantyCreationConfirmation;
